import loadable from '@loadable/component';
import { forEach, isEmpty, isArray, debounce } from 'lodash';
import { useEffect, useReducer, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Error } from '@krea/common/shared-components/fields/error';
import Fieldset from '@krea/common/shared-components/fields/fieldset';
import FileItem from '@krea/common/shared-components/fields/file-item';
import InputLink from '@krea/common/shared-components/fields/input-link';
import TextArea from '@krea/common/shared-components/fields/textarea';
import LeasingOffer from '@krea/common/shared-components/loan-application-form/fields/leasing/offer';
import { Text } from '@krea/common/shared-components/text';
import {
  useDeleteFile,
  usePostFieldRequest,
} from '@krea/common/store/field-requests/hooks/mutations';
import { useGetOneFieldRequest } from '@krea/common/store/field-requests/hooks/queries';
import { isValidUrl } from '@krea/common/utils';

import FieldRequestPreviewHeader from '../FieldRequestPreviewHeader';
import FieldRequestSaved from '../FieldRequestSaved';

const FreeTextParser = loadable(
  () => import('@krea/common/shared-components/freetext-parser'),
);

const LeasingAdditionalDataForm = ({ fieldRequestID, isPreviewMode }) => {
  const { t } = useTranslation();

  const { data: { displayName, submittedData } = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);
  const { mutate: deleteFile } = useDeleteFile();

  const errors = error?.data?.errors || [];

  function reducer(state, action) {
    switch (action.type) {
      case 'leasing_product_url':
        return { ...state, leasing_product_url: action.payload };

      case 'leasing_product_description':
        return { ...state, leasing_product_description: action.payload };

      case 'touched':
        return { ...state, touched: action.payload };

      case 'set_url_link':
        return {
          ...state,
          isValidUrlLink: isValidUrl(action.payload),
          leasing_product_url: action.payload,
          touched: state.leasing_product_url !== action.payload,
        };

      default:
        return { ...state };
    }
  }

  const [state, setState] = useReducer(reducer, {
    leasing_product_url: submittedData?.leasing_product_url || '',
    leasing_product_description:
      submittedData?.leasing_product_description || '',
    touched: false,
    isValidUrlLink: true,
  });

  const files = submittedData?.files || [];

  const deleteFileFunc = (fileID) => {
    deleteFile({ fileID, fieldRequestID });
  };

  const createFormData = (value) => {
    const formData = new FormData();

    formData.append(
      'leasing_product_description',
      value.leasing_product_description || '',
    );

    if (isArray(value.files) && !isEmpty(value.files)) {
      forEach(value.files, (file) => {
        formData.append('files', file, file.name || file.original_file_name);
      });
    }
    if (value.leasing_product_url)
      formData.append('leasing_product_url', value.leasing_product_url);

    return formData;
  };

  const onBlur = (state, files = null) => {
    const { touched } = state;
    const value = state;

    value.files = isArray(files) ? files : null;
    const formData = createFormData(value);

    if (touched || isArray(files)) {
      postFieldRequest(formData);

      if (touched) {
        setState({ type: 'touched', payload: false });
      }
    }
  };

  const onChange = ({ target }) => {
    let parsedValue = target.value;

    setState({ type: `${target.name}`, payload: parsedValue });
    setState({ type: 'touched', payload: state.value !== parsedValue });
  };

  const onChangeLink = (e) => {
    const value = e.target.value;

    setState({ type: 'set_url_link', payload: value });
  };

  const debouncedFunc = debounce((state) => onBlur(state), 2500);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback((state) => debouncedFunc(state), []);

  useEffect(() => {
    debouncedSave(state);

    return () => {
      debouncedFunc.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <>
      {isPreviewMode ? (
        <>
          {((files && files.length > 0) || state.leasing_product_url) && (
            <FieldRequestPreviewHeader
              label={displayName}
              value={
                <>
                  {files.map((file) => (
                    <FileItem file={file} isPreviewMode={isPreviewMode} />
                  ))}

                  {state.leasing_product_url && (
                    <InputLink
                      classNamePreview="mt-2"
                      isPreviewMode={isPreviewMode}
                      value={state.leasing_product_url}
                    />
                  )}
                </>
              }
            />
          )}
          {state.leasing_product_description && (
            <FieldRequestPreviewHeader
              subheader
              label={t('iaq.leasing.preview.description')}
              value={
                <FreeTextParser text={state.leasing_product_description} />
              }
            />
          )}
        </>
      ) : (
        <>
          <div className="input-form mb-5">
            <Fieldset>
              <LeasingOffer
                isFieldRequest
                files={files}
                url={state.leasing_product_url || ''}
                onChangeLink={(e) => onChangeLink(e)}
                onBlurLink={(e) => onBlur(state)}
                onPostFiles={(files) => onBlur(state, files)}
                onDeleteFile={({ fileId }) => deleteFileFunc(fileId)}
                errorLink={
                  !state.isValidUrlLink && state.leasing_product_url !== ''
                    ? t(
                        'applicationForm.leasing.fields.offer.links.link.errors.notUrl',
                      )
                    : null
                }
              />
              <Text
                size="md"
                className="font-weight-bold mt-3"
                style={{ marginBottom: '-12px' }}
              >
                {t('applicationForm.leasing.fields.objectDescription.label')}
              </Text>
              <TextArea
                name="leasing_product_description"
                placeholder={t(
                  'applicationForm.leasing.fields.objectDescription.freetext',
                )}
                value={state.leasing_product_description || ''}
                onChange={onChange}
                onBlur={() => onBlur(state)}
              />
            </Fieldset>
          </div>

          <FieldRequestSaved fieldRequestID={fieldRequestID} />
        </>
      )}

      {errors.map((error, index) => (
        <Error className="mt-3" key={index}>
          {error.message}
        </Error>
      ))}
    </>
  );
};

export default LeasingAdditionalDataForm;
